'use client';

import React, { forwardRef, ReactElement, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface TextInputPropTypes
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * If `true`, the text input will display in an error state.
   */
  error?: boolean;
  /**
   * If `true`, the text input will take up the full width of its container.
   */
  fullWidth?: boolean;
  /**
   * Indicates that the input should have fully rounded corners.
   */
  rounded?: boolean;
  /**
   * Allows a custom label, defaults to placeholder's value.
   */
  label?: string;
}

const TextInput = forwardRef<HTMLInputElement, TextInputPropTypes>(
  function ForwardedTextInput(props, forwardedRef): ReactElement {
    const {
      className,
      error,
      fullWidth,
      rounded,
      type = 'text',
      placeholder,
      label,
      value,
      ...rest
    } = props;

    const focusClasses =
      'focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-20 transition-shadow';
    const errorFocusClasses =
      'focus:border-red-500 focus:ring focus:ring-red-500 focus:ring-opacity-20 transition-shadow';

    const classes = twMerge(
      className,
      'form-input',
      'border border-solid border-gray-400 disabled:bg-gray-200 disabled:border-gray-200',
      error ? errorFocusClasses : focusClasses,
      rounded ? 'px-5 py-2 rounded-full' : 'p-2 rounded',
      fullWidth && 'block w-full'
    );

    const id = (label || placeholder || '').replace(/\s/g, '-');

    const [passwordShown, setPasswordShown] = useState(false);

    const isPassword = type?.toLowerCase() === 'password';

    return (
      <>
        <div className="flex justify-between">
          <label htmlFor={id} className="text-base font-semibold block">
            {label || placeholder}
          </label>
          {isPassword && (
            <a
              role="button"
              tabIndex={0}
              className="no-underline text-right cursor-pointer leading-loose font-semibold text-sm"
              onClick={() =>
                setPasswordShown((passwordShown) => !passwordShown)
              }
              onKeyDown={() =>
                setPasswordShown((passwordShown) => !passwordShown)
              }
            >
              {passwordShown ? 'Hide Password' : 'Show Password'}
            </a>
          )}
        </div>
        <input
          id={id}
          className={classes}
          type={passwordShown ? 'text' : type}
          placeholder={placeholder}
          ref={forwardedRef}
          value={value}
          {...rest}
        />
      </>
    );
  }
);

export default TextInput;
